/**
 * Mirrors Hutool {@code cn.hutool.core.text.PasswdStrength#check} (v5.8.x), used by
 * {@code ProfileController#updatePwd} on the client API.
 */

const DICTIONARY = [
  'password',
  'abc123',
  'iloveyou',
  'adobe123',
  '123123',
  'sunshine',
  '1314520',
  'a1b2c3',
  '123qwe',
  'aaa111',
  'qweasd',
  'admin',
  'passwd',
]

const SIZE_TABLE = [9, 99, 999, 9999, 99999, 999999, 9999999, 99999999, 999999999, 2147483647]

const CHAR = {
  NUM: 0,
  SMALL_LETTER: 1,
  CAPITAL_LETTER: 2,
  OTHER_CHAR: 3,
}

function checkCharacterType(c) {
  const code = c.charCodeAt(0)
  if (code >= 48 && code <= 57) return CHAR.NUM
  if (code >= 65 && code <= 90) return CHAR.CAPITAL_LETTER
  if (code >= 97 && code <= 122) return CHAR.SMALL_LETTER
  return CHAR.OTHER_CHAR
}

function countLetter(passwd, type) {
  if (!passwd) return 0
  let count = 0
  for (let i = 0; i < passwd.length; i++) {
    if (checkCharacterType(passwd[i]) === type) count++
  }
  return count
}

function isNumeric(str) {
  return str != null && str !== '' && /^\d+$/.test(str)
}

function isCharEquals(str) {
  if (str == null || str.length <= 1) return true
  const c = str[0]
  for (let i = 1; i < str.length; i++) {
    if (str[i] !== c) return false
  }
  return true
}

function sizeOfInt(x) {
  for (let i = 0; ; i++) {
    if (x <= SIZE_TABLE[i]) return i + 1
  }
}

/**
 * @returns {number} Hutool PasswdStrength.check score; backend requires {@code >= 2}.
 */
export function passwdStrengthCheck(passwd) {
  if (passwd == null) {
    throw new Error('password is empty')
  }
  const len = passwd.length
  let level = 0

  if (countLetter(passwd, CHAR.NUM) > 0) level++
  if (countLetter(passwd, CHAR.SMALL_LETTER) > 0) level++
  if (len > 4 && countLetter(passwd, CHAR.CAPITAL_LETTER) > 0) level++
  if (len > 6 && countLetter(passwd, CHAR.OTHER_CHAR) > 0) level++

  if (
    (len > 4 &&
      countLetter(passwd, CHAR.NUM) > 0 &&
      countLetter(passwd, CHAR.SMALL_LETTER) > 0) ||
    (countLetter(passwd, CHAR.NUM) > 0 && countLetter(passwd, CHAR.CAPITAL_LETTER) > 0) ||
    (countLetter(passwd, CHAR.NUM) > 0 && countLetter(passwd, CHAR.OTHER_CHAR) > 0) ||
    (countLetter(passwd, CHAR.SMALL_LETTER) > 0 && countLetter(passwd, CHAR.CAPITAL_LETTER) > 0) ||
    (countLetter(passwd, CHAR.SMALL_LETTER) > 0 && countLetter(passwd, CHAR.OTHER_CHAR) > 0) ||
    (countLetter(passwd, CHAR.CAPITAL_LETTER) > 0 && countLetter(passwd, CHAR.OTHER_CHAR) > 0)
  ) {
    level++
  }

  if (
    (len > 6 &&
      countLetter(passwd, CHAR.NUM) > 0 &&
      countLetter(passwd, CHAR.SMALL_LETTER) > 0 &&
      countLetter(passwd, CHAR.CAPITAL_LETTER) > 0) ||
    (countLetter(passwd, CHAR.NUM) > 0 &&
      countLetter(passwd, CHAR.SMALL_LETTER) > 0 &&
      countLetter(passwd, CHAR.OTHER_CHAR) > 0) ||
    (countLetter(passwd, CHAR.NUM) > 0 &&
      countLetter(passwd, CHAR.CAPITAL_LETTER) > 0 &&
      countLetter(passwd, CHAR.OTHER_CHAR) > 0) ||
    (countLetter(passwd, CHAR.SMALL_LETTER) > 0 &&
      countLetter(passwd, CHAR.CAPITAL_LETTER) > 0 &&
      countLetter(passwd, CHAR.OTHER_CHAR) > 0)
  ) {
    level++
  }

  if (
    len > 8 &&
    countLetter(passwd, CHAR.NUM) > 0 &&
    countLetter(passwd, CHAR.SMALL_LETTER) > 0 &&
    countLetter(passwd, CHAR.CAPITAL_LETTER) > 0 &&
    countLetter(passwd, CHAR.OTHER_CHAR) > 0
  ) {
    level++
  }

  if (
    (len > 6 &&
      countLetter(passwd, CHAR.NUM) >= 3 &&
      countLetter(passwd, CHAR.SMALL_LETTER) >= 3) ||
    (countLetter(passwd, CHAR.NUM) >= 3 && countLetter(passwd, CHAR.CAPITAL_LETTER) >= 3) ||
    (countLetter(passwd, CHAR.NUM) >= 3 && countLetter(passwd, CHAR.OTHER_CHAR) >= 2) ||
    (countLetter(passwd, CHAR.SMALL_LETTER) >= 3 && countLetter(passwd, CHAR.CAPITAL_LETTER) >= 3) ||
    (countLetter(passwd, CHAR.SMALL_LETTER) >= 3 && countLetter(passwd, CHAR.OTHER_CHAR) >= 2) ||
    (countLetter(passwd, CHAR.CAPITAL_LETTER) >= 3 && countLetter(passwd, CHAR.OTHER_CHAR) >= 2)
  ) {
    level++
  }

  if (
    (len > 8 &&
      countLetter(passwd, CHAR.NUM) >= 2 &&
      countLetter(passwd, CHAR.SMALL_LETTER) >= 2 &&
      countLetter(passwd, CHAR.CAPITAL_LETTER) >= 2) ||
    (countLetter(passwd, CHAR.NUM) >= 2 &&
      countLetter(passwd, CHAR.SMALL_LETTER) >= 2 &&
      countLetter(passwd, CHAR.OTHER_CHAR) >= 2) ||
    (countLetter(passwd, CHAR.NUM) >= 2 &&
      countLetter(passwd, CHAR.CAPITAL_LETTER) >= 2 &&
      countLetter(passwd, CHAR.OTHER_CHAR) >= 2) ||
    (countLetter(passwd, CHAR.SMALL_LETTER) >= 2 &&
      countLetter(passwd, CHAR.CAPITAL_LETTER) >= 2 &&
      countLetter(passwd, CHAR.OTHER_CHAR) >= 2)
  ) {
    level++
  }

  if (
    len > 10 &&
    countLetter(passwd, CHAR.NUM) >= 2 &&
    countLetter(passwd, CHAR.SMALL_LETTER) >= 2 &&
    countLetter(passwd, CHAR.CAPITAL_LETTER) >= 2 &&
    countLetter(passwd, CHAR.OTHER_CHAR) >= 2
  ) {
    level++
  }

  if (countLetter(passwd, CHAR.OTHER_CHAR) >= 3) level++
  if (countLetter(passwd, CHAR.OTHER_CHAR) >= 6) level++

  if (len > 12) {
    level++
    if (len >= 16) level++
  }

  if (
    'abcdefghijklmnopqrstuvwxyz'.indexOf(passwd) > 0 ||
    'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.indexOf(passwd) > 0
  ) {
    level--
  }
  if (
    'qwertyuiop'.indexOf(passwd) > 0 ||
    'asdfghjkl'.indexOf(passwd) > 0 ||
    'zxcvbnm'.indexOf(passwd) > 0
  ) {
    level--
  }
  if (
    isNumeric(passwd) &&
    ('01234567890'.indexOf(passwd) > 0 || '09876543210'.indexOf(passwd) > 0)
  ) {
    level--
  }

  if (
    countLetter(passwd, CHAR.NUM) === len ||
    countLetter(passwd, CHAR.SMALL_LETTER) === len ||
    countLetter(passwd, CHAR.CAPITAL_LETTER) === len
  ) {
    level--
  }

  if (len % 2 === 0) {
    const part1 = passwd.substring(0, len / 2)
    const part2 = passwd.substring(len / 2)
    if (part1 === part2) level--
    if (isCharEquals(part1) && isCharEquals(part2)) level--
  }
  if (len % 3 === 0) {
    const part1 = passwd.substring(0, len / 3)
    const part2 = passwd.substring(len / 3, (len / 3) * 2)
    const part3 = passwd.substring((len / 3) * 2)
    if (part1 === part2 && part2 === part3) level--
  }

  if (isNumeric(passwd) && len >= 6 && len <= 8) {
    let year = 0
    if (len === 8 || len === 6) {
      year = parseInt(passwd.substring(0, len - 4), 10)
    }
    const size = sizeOfInt(year)
    const month = parseInt(passwd.substring(size, size + 2), 10)
    const day = parseInt(passwd.substring(size + 2, len), 10)
    if (year >= 1950 && year < 2050 && month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      level--
    }
  }

  for (const s of DICTIONARY) {
    if (passwd === s || s.includes(passwd)) {
      level--
      break
    }
  }

  if (len <= 6) {
    level--
    if (len <= 4) {
      level--
      if (len <= 3) {
        level = 0
      }
    }
  }

  if (isCharEquals(passwd)) {
    level = 0
  }

  if (level < 0) level = 0

  return level
}

/** Backend rules: length >= 6 and {@code PasswdStrength.check(pw) >= 2}. */
export function isNewPasswordAllowed(pw) {
  return typeof pw === 'string' && pw.length >= 6 && passwdStrengthCheck(pw) >= 2
}

/**
 * Form hint (English). Same rules as {@code ProfileController#updatePwd}: length >= 6 and
 * Hutool {@code PasswdStrength.check} >= 2. The sentences below mirror server errors where noted.
 */
export const PASSWORD_RULE_HINT =
  'At least 6 characters; preferably include letters, numbers, and special characters (same as server). Strength must pass the server check (mixed types; weak patterns such as keyboard walks or common passwords are rejected).'

/** Verbatim {@code ProfileController.updatePwd} message when {@code StrUtil.length(newPassword) < 6}. */
export const PASSWORD_MIN_LENGTH_MSG =
  'Please enter a password of at least 6 characters, preferably including letters, numbers, and special characters!'

/** Verbatim {@code ProfileController.updatePwd} message when {@code PasswdStrength.check(newPassword) < 2}. */
export const PASSWORD_TOO_SIMPLE_MSG =
  'Password too simple,Please enter a password at least 6 characters including letters, numbers or special characters!'
